<template>
  <div>
    <PageHeader
      :title="detail ? detail.user.name : 'Lead'"
      :subtitle="subtitle"
      :back-to="{ name: 'karrofin-users' }"
    >
      <v-btn
        small
        outlined
        class="text-none"
        :to="{ name: 'karrofin-notifications', query: { user_id: id } }"
      >
        <LucideIcon name="bell" :size="16" class="mr-1" />
        Notify
      </v-btn>
    </PageHeader>

    <!-- Only present on the curated verification records, so it is obvious
         at a glance what this particular lead is meant to demonstrate. -->
    <v-alert
      v-if="detail && detail.user.showcase"
      dense
      text
      type="info"
      class="kf-showcase"
    >
      <strong>Verification example.</strong> {{ detail.user.showcase }}
    </v-alert>

    <div v-if="loading">
      <v-row dense>
        <v-col cols="12" md="4"><v-skeleton-loader type="card" /></v-col>
        <v-col cols="12" md="8"><v-skeleton-loader type="article, article" /></v-col>
      </v-row>
    </div>

    <ErrorState v-else-if="error" :message="error" @retry="load" />

    <v-row v-else-if="detail" dense>
      <!-- ------------------------------------------------ left: the user -->
      <v-col cols="12" md="4">
        <div class="kf-card neumorphism-flat">
          <div class="kf-profile">
            <span class="kf-profile__avatar">{{ fmt.initials(detail.user.name) }}</span>
            <div class="kf-profile__id">
              <div class="kf-profile__name">{{ detail.user.name }}</div>
              <div class="kf-profile__meta">{{ detail.user.customer_id }}</div>
            </div>
          </div>

          <div class="kf-tags mt-3">
            <StatusChip v-for="b in detail.user.buckets" :key="b" :status="b" small />
          </div>

          <v-divider class="my-3" />

          <dl class="kf-facts">
            <div><dt>Mobile</dt><dd>{{ fmt.mobile(detail.user.mobile) }}</dd></div>
            <div><dt>Email</dt><dd class="kf-break">{{ fmt.orDash(detail.user.email) }}</dd></div>
            <div><dt>City</dt><dd>{{ fmt.orDash(detail.user.city) }}</dd></div>
            <div><dt>Age</dt><dd>{{ fmt.orDash(detail.user.age) }}</dd></div>
            <div><dt>Monthly income</dt><dd>{{ fmt.currency(detail.user.monthly_income) }}</dd></div>
            <div>
              <dt>CIBIL</dt>
              <dd>
                <span v-if="detail.user.cibil_score">{{ detail.user.cibil_score }}</span>
                <span v-else class="kf-muted">Not pulled</span>
              </dd>
            </div>
            <div><dt>Platform</dt><dd>{{ detail.user.platform }}</dd></div>
            <div><dt>Lead source</dt><dd>{{ detail.user.lead_source }}</dd></div>
            <div><dt>Profile status</dt><dd>{{ detail.user.profile_status }}</dd></div>
            <div><dt>Funnel status</dt><dd>{{ detail.user.funnel_status }}</dd></div>
            <div><dt>Signed up</dt><dd>{{ fmt.dateTime(detail.user.created) }}</dd></div>
          </dl>
        </div>

      </v-col>

      <!-- ---------------------------------------- right: the actual chain -->
      <v-col cols="12" md="8">
        <!-- Application -->
        <div class="kf-card neumorphism-flat">
          <div class="kf-card__title">Application</div>
          <div v-if="!detail.loan" class="kf-card__hint mt-1">
            No application exists — this user has not generated offers yet.
          </div>
          <div v-else class="kf-app mt-2">
            <div class="kf-app__item">
              <span class="kf-app__label">Loan type</span>
              <span class="kf-app__value">{{ detail.loan.loan_type }}</span>
            </div>
            <div class="kf-app__item">
              <span class="kf-app__label">Requested</span>
              <span class="kf-app__value">{{ fmt.currency(detail.loan.loan_amount) }}</span>
            </div>
            <div class="kf-app__item">
              <span class="kf-app__label">Precheck status</span>
              <StatusChip :status="detail.loan.precheck_status" small />
            </div>
            <div class="kf-app__item">
              <span class="kf-app__label">Created</span>
              <span class="kf-app__value">{{ fmt.date(detail.loan.created) }}</span>
            </div>
            <div v-if="detail.loan.loan_reject_reason" class="kf-app__item kf-app__item--wide">
              <span class="kf-app__label">Rejection reason</span>
              <span class="kf-app__value kf-app__value--danger">
                {{ detail.loan.loan_reject_reason }}
              </span>
            </div>
          </div>
        </div>

        <!-- Offers -->
        <div class="kf-card neumorphism-flat mt-3">
          <div class="kf-card__head">
            <div>
              <div class="kf-card__title">Offers</div>
              <div class="kf-card__hint">
                Every offer stays live even after one of them disburses or is
                rejected.
              </div>
            </div>
            <span class="kf-card__badge">{{ detail.offers.length }}</span>
          </div>

          <EmptyState
            v-if="!detail.offers.length"
            class="py-6"
            title="No offers generated"
            message="This user has not reached offer generation."
            icon="tag-off"
          />

          <div v-else class="kf-offer-list mt-2">
            <div
              v-for="offer in detail.offers"
              :key="offer.id"
              class="kf-offer"
              :class="{ 'kf-offer--clicked': offer.flag_offer_availed }"
            >
              <div class="kf-offer__bar" :style="{ backgroundColor: offerAccent(offer) }"></div>
              <div class="kf-offer__main">
                <div class="kf-offer__row">
                  <span class="kf-offer__title">{{ offer.title }}</span>
                  <StatusChip :status="offerStatus(offer)" small />
                </div>
                <div class="kf-offer__meta">
                  <span>{{ offer.product_type }}</span>
                  <span>·</span>
                  <span>{{ offer.interest_rate }}</span>
                  <span v-if="offer.loan_upto && offer.loan_upto !== '-'">·</span>
                  <span v-if="offer.loan_upto && offer.loan_upto !== '-'">
                    up to {{ fmt.currency(offer.loan_upto) }}
                  </span>
                </div>
                <div class="kf-offer__foot">
                  <span v-if="offer.flag_offer_availed" class="kf-offer__clicked">
                    <LucideIcon name="mouse-pointer-click" :size="13" color="#00796B" />
                    Clicked {{ fmt.relative(offer.clicked_at) }}
                  </span>
                  <span v-else class="kf-muted">Not clicked</span>

                  <span v-if="offer.flag_disbursed" class="kf-offer__disbursed">
                    Disbursed {{ fmt.currency(offer.disbursal_amount) }}
                    on {{ fmt.date(offer.disbursal_date) }}
                  </span>
                  <span v-else-if="offer.remark" class="kf-offer__reason">
                    {{ offer.remark }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Timeline -->
        <div class="kf-card neumorphism-flat mt-3">
          <div class="kf-card__title">Activity timeline</div>
          <div class="kf-timeline mt-3">
            <div v-for="step in detail.timeline" :key="step.key" class="kf-step">
              <div class="kf-step__marker">
                <span class="kf-step__dot" :style="{ backgroundColor: step.color }">
                  <LucideIcon :name="step.icon" :size="12" color="#fff" />
                </span>
              </div>
              <div class="kf-step__body">
                <div class="kf-step__label">{{ step.label }}</div>
                <div class="kf-step__detail">{{ step.detail }}</div>
                <div v-if="step.at" class="kf-step__at">{{ fmt.dateTime(step.at) }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Notifications -->
        <div class="kf-card neumorphism-flat mt-3">
          <div class="kf-card__title">Notifications sent</div>
          <EmptyState
            v-if="!detail.notifications.length"
            class="py-6"
            title="Nothing sent yet"
            message="No notifications have gone to this user."
            icon="bell-off"
          />
          <div v-else class="kf-notif-list mt-2">
            <div v-for="n in detail.notifications" :key="n.id" class="kf-notif">
              <LucideIcon :name="channelIcon(n.channel)" :size="16" :color="channelColor(n.channel)" />
              <div class="kf-notif__body">
                <div class="kf-notif__title">{{ n.title }}</div>
                <div class="kf-notif__meta">
                  {{ n.channel }} · {{ fmt.dateTime(n.sent_at) }}
                </div>
              </div>
              <StatusChip :status="n.status" small />
            </div>
          </div>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import PageHeader from "@/components/karrofin/PageHeader.vue";
import StatusChip from "@/components/karrofin/StatusChip.vue";
import EmptyState from "@/components/karrofin/EmptyState.vue";
import ErrorState from "@/components/karrofin/ErrorState.vue";
import api from "@/app-js/karrofin/api";
import fmt from "@/app-js/karrofin/format";
import {
  CHANNELS,
  OFFER_LOAN_STATUS,
  STATUS_COLOR,
  DEFAULT_STATUS_COLOR,
} from "@/app-js/karrofin/constants";

export default {
  name: "KarroFinLeadDetail",
  components: { PageHeader, StatusChip, EmptyState, ErrorState },
  data() {
    return {
      fmt,
      loading: true,
      error: null,
      detail: null,
    };
  },
  computed: {
    id() {
      return this.$route.params.id;
    },
    subtitle() {
      if (!this.detail) return "";
      const u = this.detail.user;
      return `${u.clicked_count} of ${u.offer_count} offers clicked · ${u.city} · joined ${fmt.date(
        u.signed_up_date
      )}`;
    },
  },
  watch: {
    // Vue reuses the component when navigating between two detail routes.
    "$route.params.id": function () {
      this.load();
    },
  },
  created() {
    this.load();
  },
  methods: {
    load() {
      this.loading = true;
      this.error = null;
      api
        .getLeadDetail(this.id)
        .then((data) => {
          this.detail = data;
        })
        .catch((err) => {
          this.error = err;
          this.detail = null;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    // An offer's effective status: disbursal wins, then a live sub-status,
    // then the stored loan_status. Mirrors how the backend spreads this.
    /**
     * Accent bar colour for an offer card.
     *
     * This used to be the lender's brand colour. With lenders gone the bar
     * still earns its place by encoding the offer's own state -- disbursed,
     * clicked, or untouched -- so the list stays scannable at a glance.
     */
    offerAccent(offer) {
      if (offer.flag_disbursed) return "#2E7D32";
      if (offer.flag_offer_availed) return "#00796B";
      return "#B0BEC5";
    },
    offerStatus(offer) {
      if (offer.flag_disbursed) return OFFER_LOAN_STATUS.DISBURSED;
      return offer.loan_sub_status || offer.loan_status;
    },
    channelIcon(channel) {
      const c = CHANNELS.find((x) => x.value === channel);
      return c ? c.icon : "bell";
    },
    channelColor(channel) {
      const map = {
        Whatsapp: "#25D366",
        Push: "#1976D2",
        SMS: "#F57C00",
        Email: "#7B1FA2",
      };
      return map[channel] || "#607D8B";
    },
  },
};
</script>

<style scoped>
.kf-showcase {
  font-size: var(--kf-fs-meta) !important;
  line-height: 1.55;
  margin-bottom: 14px;
}
.kf-card {
  background: var(--kf-surface);
  border-radius: 10px;
  padding: 14px 16px;
}
.kf-card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}
.kf-card__title {
  font-size: 15px;
  font-weight: 700;
  color: var(--kf-brand);
}
.kf-card__badge {
  background: var(--kf-brand-soft);
  color: var(--kf-brand);
  border-radius: 20px;
  padding: 2px 10px;
  font-size: var(--kf-fs-meta);
  font-weight: 700;
}
.kf-profile {
  display: flex;
  align-items: center;
  gap: 12px;
}
.kf-profile__avatar {
  width: 46px;
  height: 46px;
  border-radius: 50%;
  background: var(--kf-brand);
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.kf-profile__name {
  font-size: 16px;
  font-weight: 700;
  color: #000;
}
.kf-profile__meta {
  font-size: var(--kf-fs-meta);
  color: #000;
}
.kf-tags {
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
}
.kf-facts {
  margin: 0;
}
.kf-facts > div {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 5px 0;
  border-bottom: 1px dashed var(--kf-border);
}
.kf-facts > div:last-child {
  border-bottom: none;
}
.kf-facts dt {
  font-size: var(--kf-fs-meta);
  color: #000;
  flex-shrink: 0;
}
.kf-facts dd {
  font-size: var(--kf-fs-meta);
  color: #000;
  font-weight: 600;
  margin: 0;
  text-align: right;
}
.kf-break {
  word-break: break-all;
}
.kf-note {
  font-size: var(--kf-fs-label) !important;
  line-height: 1.5;
}
.kf-app {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 26px;
}
.kf-app__item {
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.kf-app__item--wide {
  flex-basis: 100%;
}
.kf-app__label {
  font-size: var(--kf-fs-label);
  color: #000;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.kf-app__value {
  font-size: var(--kf-fs-hint);
  font-weight: 600;
  color: #000;
}
.kf-app__value--danger {
  color: #c62828;
}
.kf-offer {
  display: flex;
  background: var(--kf-surface-2);
  border: 1px solid var(--kf-border);
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 8px;
}
.kf-offer--clicked {
  background: #ffffff;
}
.kf-offer__bar {
  width: 4px;
  flex-shrink: 0;
}
.kf-offer__main {
  padding: 10px 12px;
  flex: 1;
  min-width: 0;
}
.kf-offer__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
}
.kf-offer__title {
  font-size: var(--kf-fs-hint);
  font-weight: 700;
  color: #000;
}
.kf-offer__meta {
  font-size: var(--kf-fs-label);
  color: #000;
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
  margin-top: 3px;
}
.kf-offer__foot {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 6px;
  font-size: var(--kf-fs-label);
}
.kf-offer__clicked {
  color: #00897b;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 3px;
}
.kf-offer__disbursed {
  color: #2e7d32;
  font-weight: 700;
}
.kf-offer__reason {
  color: #c62828;
}
.kf-timeline {
  position: relative;
}
.kf-step {
  display: flex;
  gap: 12px;
  padding-bottom: 16px;
  position: relative;
}
.kf-step:not(:last-child)::before {
  content: "";
  position: absolute;
  left: 11px;
  top: 24px;
  bottom: 0;
  width: 2px;
  background: var(--kf-border);
}
.kf-step__dot {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 1;
}
.kf-step__label {
  font-size: var(--kf-fs-hint);
  font-weight: 600;
  color: #000;
}
.kf-step__detail {
  font-size: var(--kf-fs-label);
  color: #000;
  margin-top: 1px;
}
.kf-step__at {
  font-size: var(--kf-fs-label);
  color: #000;
  margin-top: 2px;
}
.kf-notif {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px solid var(--kf-border);
}
.kf-notif:last-child {
  border-bottom: none;
}
.kf-notif__body {
  flex: 1;
  min-width: 0;
}
.kf-notif__title {
  font-size: var(--kf-fs-meta);
  font-weight: 600;
  color: #000;
}
.kf-notif__meta {
  font-size: var(--kf-fs-label);
  color: #000;
}
.kf-muted {
  color: #000;
}
</style>
